/**
 * Vincent B1020 (Eric 2026-02-26 SPEC STRICK PROD) — Dashboard santé admin.
 *
 * Page unique qui synthétise en 1 clic :
 *   - 5xx récents (path + status + top URLs).
 *   - Events browser-throttled 24h (top URLs) — B1016 backing.
 *   - Crons : liste + heartbeats + flag `stale` (> 90 min) et `failed`.
 *   - Workers en retard : vidéos en processing depuis > 30 min.
 *
 * Statut global : green | yellow | red selon # de signaux rouges.
 * Refresh manuel (bouton) et auto toutes les 60s.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import LegacyStreamTokenCard from "../../components/admin/LegacyStreamTokenCard"; // Tâche 1224

const API = process.env.REACT_APP_BACKEND_URL;
const AUTO_REFRESH_MS = 60_000;

const _STATUS_COLORS = {
  green: "bg-emerald-100 text-emerald-900 border-emerald-300",
  yellow: "bg-amber-100 text-amber-900 border-amber-300",
  red: "bg-red-100 text-red-900 border-red-300",
};

const _STATUS_LABELS_FR = {
  green: "Tout va bien",
  yellow: "1 signal à surveiller",
  red: "Plusieurs signaux critiques",
};

export default function AdminHealthDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  // Vincent B1030 (Eric 2026-02-26 SPEC STRICK PROD) — Journal des 10
  // derniers événements de cycle de vie de la file (detach/reattach/erase).
  const [lifecycleEvents, setLifecycleEvents] = useState([]);
  // Vincent B1031 (Eric 2026-02-26 SPEC STRICK PROD) — Filtre par type
  // d'événement pour retrouver rapidement un cas précis. "all" par défaut.
  const [lifecycleFilter, setLifecycleFilter] = useState("all");
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [health, lifecycle] = await Promise.all([
        axios.get(`${API}/api/admin/health-dashboard`, { withCredentials: true }),
        axios.get(`${API}/api/admin/queue-lifecycle-events`, { withCredentials: true }).catch(() => ({ data: { events: [] } })),
      ]);
      setData(health.data);
      setLifecycleEvents(lifecycle.data?.events || []);
      setLastLoadedAt(new Date());
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, AUTO_REFRESH_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [load]);

  const overall = data?.overall || "green";
  const signals = data?.signals || {};

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="admin-health-dashboard">
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">
            🩺 Santé de l'application
          </h1>
          <p className="text-sm text-zinc-600 mt-1">
            Synthèse en temps réel : erreurs 5xx, saturation navigateur, crons et workers en retard.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold border ${_STATUS_COLORS[overall]}`}
            data-testid="admin-health-overall"
          >
            <span className={`w-2.5 h-2.5 rounded-full ${overall === "green" ? "bg-emerald-500" : overall === "yellow" ? "bg-amber-500" : "bg-red-500"}`} />
            {_STATUS_LABELS_FR[overall]}
          </span>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="text-xs font-semibold px-3 py-1.5 rounded bg-white border border-zinc-300 text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
            data-testid="admin-health-refresh-btn"
          >
            {loading ? "Chargement…" : "🔄 Rafraîchir"}
          </button>
        </div>
      </header>

      {lastLoadedAt && (
        <p className="text-[11px] text-zinc-500 font-mono" data-testid="admin-health-last-loaded">
          Dernière mise à jour : {lastLoadedAt.toLocaleString("fr-CA")}
          {" · "}
          Auto-refresh 60s
        </p>
      )}

      {/* Cartes de synthèse */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-health-cards">
        <SignalCard
          testid="admin-health-card-5xx"
          title="Erreurs 5xx récentes"
          value={data?.errors_5xx?.total_captured ?? 0}
          maxHint={`buffer ${data?.errors_5xx?.buffer_max ?? 200}`}
          alerted={signals.high_5xx}
        />
        <SignalCard
          testid="admin-health-card-throttle"
          title="Saturation navigateur (24 h)"
          value={data?.throttle?.total_24h ?? 0}
          maxHint="events browser-throttled"
          alerted={signals.high_throttle}
        />
        <SignalCard
          testid="admin-health-card-cron"
          title="Crons"
          value={(data?.crons || []).filter((c) => c.stale || c.failed).length}
          maxHint={`sur ${data?.crons?.length ?? 0} jobs`}
          alerted={signals.stale_cron}
          alertLabel="en retard ou échoués"
        />
        <SignalCard
          testid="admin-health-card-stuck"
          title="Workers en retard"
          value={data?.stuck_workers?.count ?? 0}
          maxHint={`> ${data?.stuck_workers?.cutoff_minutes ?? 30} min`}
          alerted={signals.stuck_workers}
        />
      </section>

      {/* Détails 5xx */}
      <DetailPanel
        testid="admin-health-5xx-details"
        title="Erreurs 5xx récentes"
        empty={!(data?.errors_5xx?.recent || []).length}
        emptyLabel="Aucune erreur 5xx enregistrée dans le buffer."
      >
        {data?.errors_5xx?.counts_by_status && Object.keys(data.errors_5xx.counts_by_status).length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {Object.entries(data.errors_5xx.counts_by_status).map(([code, count]) => (
              <span
                key={code}
                className="text-xs font-mono px-2 py-0.5 rounded bg-red-50 border border-red-200 text-red-800"
                data-testid={`admin-health-5xx-status-${code}`}
              >
                {code} × {count}
              </span>
            ))}
          </div>
        )}
        <ul className="space-y-1" data-testid="admin-health-5xx-list">
          {(data?.errors_5xx?.recent || []).map((e, i) => (
            <li key={i} className="text-xs font-mono flex items-center gap-2 border-b border-zinc-100 pb-1">
              <span className="text-red-700 font-semibold">{e.status}</span>
              <span className="text-zinc-700 flex-1 truncate">{e.path}</span>
              <span className="text-zinc-500">{e.at?.slice(11, 19)}</span>
            </li>
          ))}
        </ul>
      </DetailPanel>

      {/* Détails throttle */}
      <DetailPanel
        testid="admin-health-throttle-details"
        title="Top URLs qui saturent le navigateur (24 h)"
        empty={!(data?.throttle?.top_urls || []).length}
        emptyLabel="Aucun event browser-throttled. Excellent signe (bouclier B1015-B1019 en place)."
      >
        <ul className="space-y-1" data-testid="admin-health-throttle-list">
          {(data?.throttle?.top_urls || []).map((t, i) => (
            <li key={i} className="text-xs font-mono flex items-center gap-2 border-b border-zinc-100 pb-1">
              <span className="text-amber-700 font-semibold">× {t.count}</span>
              <span className="text-zinc-700 flex-1 truncate">{t.url}</span>
            </li>
          ))}
        </ul>
      </DetailPanel>

      {/* Détails crons */}
      <DetailPanel
        testid="admin-health-cron-details"
        title="Crons enregistrés"
        empty={!(data?.crons || []).length}
        emptyLabel="Aucun cron heartbeat enregistré."
      >
        <ul className="space-y-1" data-testid="admin-health-cron-list">
          {(data?.crons || []).map((c, i) => {
            const bad = c.stale || c.failed;
            return (
              <li
                key={i}
                className={`text-xs flex items-center gap-2 border-b border-zinc-100 pb-1 ${bad ? "bg-amber-50" : ""}`}
                data-testid={`admin-health-cron-item-${c.job}`}
              >
                <span className={`font-mono font-semibold ${bad ? "text-amber-800" : "text-emerald-800"}`}>
                  {bad ? "⚠" : "✔"}
                </span>
                <span className="font-mono font-semibold text-zinc-900 truncate flex-1">{c.job}</span>
                <span className="text-zinc-600 font-mono">{c.last_status || "?"}</span>
                <span className="text-zinc-500 font-mono text-[10px]">
                  {c.last_run_at ? new Date(c.last_run_at).toLocaleString("fr-CA") : "?"}
                </span>
              </li>
            );
          })}
        </ul>
      </DetailPanel>

      {/* Détails stuck workers */}
      <DetailPanel
        testid="admin-health-stuck-details"
        title="Vidéos bloquées en traitement"
        empty={!(data?.stuck_workers?.videos || []).length}
        emptyLabel="Aucune vidéo bloquée. Pipeline en bonne santé."
      >
        <ul className="space-y-1" data-testid="admin-health-stuck-list">
          {(data?.stuck_workers?.videos || []).map((v) => (
            <li
              key={v.id}
              className="text-xs flex items-center gap-2 border-b border-zinc-100 pb-1"
              data-testid={`admin-health-stuck-item-${v.id}`}
            >
              <span className="text-red-700 font-mono font-semibold">
                {v.stuck_minutes ?? "?"} min
              </span>
              <span className="text-zinc-500 font-mono text-[10px]">{v.pipeline_status}/{v.pipeline_stage || "?"}</span>
              <Link
                to={`/dashboard/video/${v.id}`}
                className="text-zinc-800 flex-1 truncate hover:underline"
              >
                {v.title || v.id}
              </Link>
              {/* Vincent B1023 (Eric 2026-02-26 SPEC STRICK PROD) —
                  Bouton reset direct depuis le dashboard santé. Purge
                  les flags de génération bloqués et remet
                  pipeline_status="failed_needs_rerun". */}
              <button
                type="button"
                onClick={async () => {
                  // eslint-disable-next-line no-alert
                  if (!window.confirm(`Réinitialiser le pipeline bloqué de « ${v.title || v.id} » ? Les flags de génération seront effacés pour permettre une nouvelle exécution.`)) return;
                  try {
                    const { data: r } = await axios.post(
                      `${API}/api/admin/videos/${v.id}/reset-pipeline`,
                      {},
                      { withCredentials: true },
                    );
                    toast.success(`Pipeline réinitialisé (${(r?.flags_reset || []).length} flag(s) purgé(s)).`);
                    await load();
                  } catch (e) {
                    toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
                  }
                }}
                className="text-[10px] font-semibold px-2 py-0.5 rounded bg-white border border-red-300 text-red-700 hover:bg-red-50"
                data-testid={`admin-health-stuck-reset-${v.id}`}
                title="Réinitialiser le pipeline bloqué pour permettre une relance"
              >
                Réinitialiser
              </button>
            </li>
          ))}
        </ul>
      </DetailPanel>

      {/* Vincent B1030 (Eric 2026-02-26 SPEC STRICK PROD) — Journal des
          10 derniers événements de cycle de vie de la file d'attente.
          Trace qui retire (detached), remet (reattached) ou efface
          (erased) quoi. Alimenté par les 3 endpoints côté videos.py. */}
      <DetailPanel
        testid="admin-health-queue-lifecycle-panel"
        title={`Historique file d'attente (${lifecycleEvents.length} événements)`}
        empty={lifecycleEvents.length === 0}
        emptyLabel="Aucun événement récent (retrait, remise ou effacement de vidéo)."
      >
        {/* Vincent B1031 (Eric 2026-02-26 SPEC STRICK PROD) — Boutons
            filtre par type d'événement. Compteurs vivants entre parenthèses. */}
        <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="admin-health-queue-lifecycle-filters">
          {[
            { id: "all",        label: "Tous",             count: lifecycleEvents.length, ring: "ring-zinc-400" },
            { id: "erased",     label: "Effacées",         count: lifecycleEvents.filter((e) => e.event === "erased").length, ring: "ring-red-400" },
            { id: "detached",   label: "Retirées",         count: lifecycleEvents.filter((e) => e.event === "detached").length, ring: "ring-zinc-500" },
            { id: "reattached", label: "Remises",          count: lifecycleEvents.filter((e) => e.event === "reattached").length, ring: "ring-emerald-400" },
          ].map((f) => {
            const active = lifecycleFilter === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setLifecycleFilter(f.id)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition ${
                  active
                    ? `bg-zinc-900 text-white border-zinc-900 ring-2 ring-offset-1 ${f.ring}`
                    : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"
                }`}
                data-testid={`admin-health-queue-lifecycle-filter-${f.id}`}
                aria-pressed={active}
              >
                {f.label} ({f.count})
              </button>
            );
          })}
        </div>
        {(() => {
          const filtered = lifecycleFilter === "all"
            ? lifecycleEvents
            : lifecycleEvents.filter((e) => e.event === lifecycleFilter);
          if (filtered.length === 0) {
            return (
              <p
                className="text-xs italic text-zinc-500"
                data-testid="admin-health-queue-lifecycle-filtered-empty"
              >
                Aucun événement de ce type dans les 10 derniers.
              </p>
            );
          }
          return (
            <ul className="divide-y divide-zinc-100">
              {filtered.map((ev) => {
                const eventStyle = ev.event === "erased"
                  ? { badge: "bg-red-100 text-red-800 border-red-300", label: "Effacée" }
                  : ev.event === "detached"
                  ? { badge: "bg-zinc-100 text-zinc-800 border-zinc-300", label: "Retirée du pipeline" }
                  : { badge: "bg-emerald-100 text-emerald-800 border-emerald-300", label: "Remise dans la file" };
                return (
                  <li
                    key={ev.id}
                    className="flex flex-wrap items-center gap-2 py-2 text-xs"
                    data-testid={`admin-health-queue-lifecycle-event-${ev.id}`}
                  >
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${eventStyle.badge}`}>
                      {eventStyle.label}
                    </span>
                    <span className="font-medium text-zinc-800 truncate max-w-md" title={ev.video_title}>
                      {ev.video_title || `(sans titre — ${ev.video_id?.slice(0, 8)})`}
                    </span>
                    <span className="text-zinc-500">
                      par <span className="font-mono">{ev.by_email || "?"}</span>
                    </span>
                    <span className="text-zinc-400 ml-auto">
                      {ev.at ? new Date(ev.at).toLocaleString("fr-CA") : "—"}
                    </span>
                  </li>
                );
              })}
            </ul>
          );
        })()}
      </DetailPanel>

      {/* Tâche 1224 — Même carte que sur la page Sentinelles (choix d'Eric :
          les deux). Combien de liens utilisent encore le jeton de session dans
          l'URL de lecture, et le bouton pour le couper. */}
      <LegacyStreamTokenCard />
    </div>
  );
}


function SignalCard({ testid, title, value, maxHint, alerted, alertLabel }) {
  return (
    <div
      className={`rounded-lg border p-4 ${alerted ? "border-red-300 bg-red-50" : "border-zinc-200 bg-white"}`}
      data-testid={testid}
    >
      <p className="text-xs font-semibold text-zinc-600 uppercase tracking-wide">{title}</p>
      <p className={`text-3xl font-bold mt-2 ${alerted ? "text-red-700" : "text-zinc-900"}`}>{value}</p>
      <p className="text-[11px] text-zinc-500 mt-1">
        {alerted && alertLabel ? alertLabel : maxHint}
      </p>
    </div>
  );
}


function DetailPanel({ testid, title, empty, emptyLabel, children }) {
  return (
    <details className="rounded-lg border border-zinc-200 bg-white" data-testid={testid} open>
      <summary className="cursor-pointer select-none px-4 py-2 font-semibold text-sm text-zinc-800">
        {title}
      </summary>
      <div className="px-4 pb-3">
        {empty ? (
          <p className="text-xs italic text-zinc-500" data-testid={`${testid}-empty`}>
            {emptyLabel}
          </p>
        ) : children}
      </div>
    </details>
  );
}
